import React, { ReactNode } from 'react';
import { useFormContext, useFieldArray, useController } from 'react-hook-form';

import {
  Button,
  TitleWithDescription,
  LbFormControlLabel,
  InfoBox,
  Switch,
} from '@infoods/lifebase-ui';
import AddIcon from '@mui/icons-material/Add';

import { ChallengeFormValues } from '../../../interfaces/ChallengeFormValues';
import { Mode } from '../../../../general-types';

import classes from './PrizesFrom.module.scss';
import { EditPrizeRow } from './EditPrizeRow/EditPrizeRow';
import { ViewPrizeRow } from './ViewPrizeRow/ViewPrizeRow';

type PrizesFromProps = {
  mode: Mode;
  headerActions?: ReactNode;
};

export const PrizesFrom = ({ mode, headerActions }: PrizesFromProps) => {
  const isViewMode = mode === 'VIEW';
  const { control } = useFormContext<ChallengeFormValues>();

  const {
    field: { value: showPrizes, onChange, onBlur, ref, name: fieldName },
  } = useController({
    control,
    name: 'prizes.enabled',
    defaultValue: false,
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'prizes.prizes',
  });

  const handleAddPrize = () => {
    append({ title: '', description: '', prizeNum: fields.length + 1 });
  };

  const handleRemovePrize = (index: number) => {
    remove(index);
  };

  const onSwitchChange = (_: any, checked: boolean) => {
    if (fields.length === 0) {
      handleAddPrize();
    }

    onChange(checked);
  };

  const SwitchTitle = (
    <LbFormControlLabel
      control={
        <Switch
          checked={showPrizes}
          onChange={onSwitchChange}
          slotProps={{ input: { ref } }}
          name={fieldName}
          onBlur={onBlur}
          disabled={isViewMode}
        />
      }
      label={
        <TitleWithDescription
          title="Prizes"
          description="Do you want to reward the champions? Add prizes to boost motivation, engagement, and commitment throughout the Challenge."
        />
      }
      align="start"
    />
  );

  return (
    <InfoBox
      title={SwitchTitle}
      adornment={headerActions}
      hideConfig={{ show: showPrizes }}
      customContentPadding
    >
      {isViewMode ? (
        fields.map((field) => (
          <ViewPrizeRow
            key={field.id}
            title={field.title}
            description={field.description}
          />
        ))
      ) : (
        <>
          {fields.map((field, index) => (
            <EditPrizeRow
              key={field.id}
              index={index}
              control={control}
              onRemove={handleRemovePrize}
              showRemoveButton={fields.length > 1}
            />
          ))}
          <div className={classes.addButtonRow}>
            <Button
              variant="text"
              color="primary"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddPrize}
            >
              Add Prize
            </Button>
          </div>
        </>
      )}
    </InfoBox>
  );
};
